import { useMutation, useQuery } from '@tanstack/react-query';
import { Checkbox, Col, Form, Row } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useIntl } from 'react-intl';
import { generalConfigApi } from '../../../../apis';
import { LineConfigDto, LineConfigDtoKeyEnum, UpdateLineConfigDto, UserRoleEnum } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { LINE_GROUPS, SETTING_MESSAGE } from '../../../../constants/QueryKey';
import './style.scss';

const SettingMessage = () => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const { data: settingMessageData, refetch } = useQuery({
    queryKey: [SETTING_MESSAGE],
    queryFn: () => generalConfigApi.generalConfigControllerGetLineSettings(),
  });

  const { data: groupMessageData } = useQuery({
    queryKey: [LINE_GROUPS],
    queryFn: () => generalConfigApi.generalConfigControllerGetLineGroups(),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateLineConfigDto) => {
      return generalConfigApi.generalConfigControllerUpdateLineSettings(payload);
    },
    onSuccess: () => {
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'settingMessage.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'settingMessage.error' }),
      });
    },
  });

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = values.values;
        const convert = Object.keys(payload).map((key) => {
          const value = Object.keys(payload[key])
            .map((keyChild) => (payload[key][keyChild] ? keyChild : ''))
            .filter((key) => key);
          return {
            key,
            value,
          } as LineConfigDto;
        });
        updateMutation.mutate({ data: convert });
      })
      .catch((error) => {
        if (error) {
          ToastError({
            content: intl.formatMessage({ id: 'settingMessage.error' }),
          });
        }
      });
  };

  const keyTextMapping = {
    [LineConfigDtoKeyEnum.RequestCheckin]: intl.formatMessage({ id: 'settingMessage.checkIn' }),
    [LineConfigDtoKeyEnum.RequestCheckout]: intl.formatMessage({ id: 'settingMessage.checkOut' }),
    [LineConfigDtoKeyEnum.GoingLate]: intl.formatMessage({ id: 'settingMessage.goingLate' }),
    [LineConfigDtoKeyEnum.LeavingSoon]: intl.formatMessage({ id: 'settingMessage.leavingSoon' }),
    [LineConfigDtoKeyEnum.Overtime]: intl.formatMessage({ id: 'settingMessage.overtime' }),
    [LineConfigDtoKeyEnum.OutsidePrivate]: intl.formatMessage({ id: 'settingMessage.outsidePrivate' }),
    [LineConfigDtoKeyEnum.OutsideBusiness]: intl.formatMessage({ id: 'settingMessage.outsideBusiness' }),
    [LineConfigDtoKeyEnum.RequestDayOff]: intl.formatMessage({ id: 'settingMessage.requestDayOff' }),
    [LineConfigDtoKeyEnum.RequestBusiness]: intl.formatMessage({ id: 'settingMessage.requestBusiness' }),
    [LineConfigDtoKeyEnum.AnnualLeave]: intl.formatMessage({ id: 'settingMessage.annualLeave' }),
    [LineConfigDtoKeyEnum.CompensatoryLeave]: intl.formatMessage({ id: 'settingMessage.compensatoryLeave' }),
    [LineConfigDtoKeyEnum.MakeupWork]: intl.formatMessage({ id: 'settingMessage.makeupWork' }),
    [LineConfigDtoKeyEnum.WorkOnDayOff]: intl.formatMessage({ id: 'settingMessage.workOnDayOff' }),
    [LineConfigDtoKeyEnum.BusinessTrip]: intl.formatMessage({ id: 'settingMessage.businessTrip' }),
    [LineConfigDtoKeyEnum.RequestTimekeepingEdit]: intl.formatMessage({ id: 'settingMessage.requestTimekeepingEdit' }),
  };

  const columns: ColumnsType = [
    {
      dataIndex: 'name',
      key: 'name',
      width: 160,
      fixed: 'left',
      render: (_, record) => {
        const text = keyTextMapping[record.key] || record.key;
        return <span>{text}</span>;
      },
    },
    {
      title: intl.formatMessage({ id: 'settingMessage.employees' }),
      dataIndex: intl.formatMessage({ id: 'settingMessage.employees' }),
      key: intl.formatMessage({ id: 'settingMessage.employees' }),
      width: 160,
      align: 'center',
      render: (_, record) => {
        return (
          <Form.Item
            noStyle
            name={['values', record.key, UserRoleEnum.Employee]}
            valuePropName={'checked'}
            initialValue={(record.value as string[]).includes(UserRoleEnum.Employee)}
          >
            <Checkbox />
          </Form.Item>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'settingMessage.approvers' }),
      dataIndex: intl.formatMessage({ id: 'settingMessage.approvers' }),
      key: intl.formatMessage({ id: 'settingMessage.approvers' }),
      width: 160,
      align: 'center',
      render: (_, record) => {
        return (
          <Form.Item
            noStyle
            name={['values', record.key, UserRoleEnum.Manager]}
            valuePropName={'checked'}
            initialValue={(record.value as string[]).includes(UserRoleEnum.Manager)}
          >
            <Checkbox />
          </Form.Item>
        );
      },
    },
  ];

  groupMessageData?.data?.data?.forEach((group) => {
    columns.push({
      title: group.groupName,
      dataIndex: group.groupId,
      key: group.groupId,
      width: 160,
      align: 'center',
      render: (_, record) => {
        return (
          <Form.Item
            noStyle
            name={['values', record.key, group.groupId]}
            valuePropName={'checked'}
            initialValue={(record.value as string[]).includes(group.groupId)}
          >
            <Checkbox />
          </Form.Item>
        );
      },
    });
  });

  return (
    <>
      <Widget
        breadcrumbs={[
          intl.formatMessage({ id: 'adminMenu.configurationManagement' }),
          intl.formatMessage({ id: 'settingMessage.title' }),
        ]}
        title={intl.formatMessage({ id: 'settingMessage.title' })}
        content={
          <Row gutter={[16, 16]} className="setting-message">
            <Col span={24} className="setting-message-title">
              {intl.formatMessage({ id: 'settingMessage.notificationRecipients' })}
            </Col>

            <Col span={24}>
              <Form form={form} layout="vertical">
                <TableWrap
                  isPagination={false}
                  loading={false}
                  tableProps={{
                    dataSource: settingMessageData?.data?.data,
                    columns,
                    scroll: { x: 1000 },
                  }}
                />

                <ButtonWrap className="setting-message-button" width={150} onClick={handleSave}>
                  {intl.formatMessage({ id: 'settingMessage.keep' })}
                </ButtonWrap>
              </Form>
            </Col>
          </Row>
        }
      />
    </>
  );
};

export default SettingMessage;
